import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import BlogCard from "../components/BlogCard";
import ShareButtons from "../components/ShareButtons";
import TableOfContents from "../components/TableOfContents";
import BackToTop from "../components/BackToTop";
import { getPostBySlug, getRelatedPosts } from "../services/blogService";

function extractSections(htmlContent) {
  if (!htmlContent) return [];
  const regex = /<h2[^>]*>(.*?)<\/h2>/gi;
  const sections = [];
  let match;
  while ((match = regex.exec(htmlContent)) !== null) {
    sections.push({
      heading: match[1].replace(/<[^>]*>/g, ""),
    });
  }
  return sections;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    getPostBySlug(slug)
      .then((found) => {
        if (cancelled) return;
        if (found) {
          setPost(found);
          return getRelatedPosts(slug, 3);
        } else {
          setNotFound(true);
          return [];
        }
      })
      .then((related) => { if (!cancelled) setRelated(related); })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <section className="min-h-screen pt-24 pb-20">
        <div className="w-full h-[40vh] md:h-[55vh] bg-white/5 animate-pulse" />
        <div className="max-w-7xl mx-auto px-6 lg:px-20 -mt-20 relative z-10">
          <div className="flex gap-10 items-start">
            <div className="flex-1 max-w-3xl">
              <div className="glass rounded-2xl p-8 md:p-12">
                <div className="h-6 w-32 bg-white/10 rounded mb-6" />
                <div className="h-10 w-3/4 bg-white/10 rounded mb-4" />
                <div className="h-10 w-1/2 bg-white/10 rounded mb-8" />
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-4 bg-white/5 rounded" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (notFound || !post) {
    return (
      <section className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-4xl font-bold">Article not found</h1>
          <p className="text-gray-400 mt-4">
            The article you're looking for doesn't exist or has been moved.
          </p>
          <Link
            to="/blog"
            className="mt-6 inline-block text-cyber-blue hover:underline"
          >
            ← Back to blog
          </Link>
        </div>
      </section>
    );
  }

  const sections = extractSections(post.content);

  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6 },
  };

  return (
    <article className="min-h-screen pt-24 pb-20">
      <BackToTop />

      {/* Hero Image */}
      <section className="relative w-full h-[40vh] md:h-[55vh] overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
      </section>

      {/* Article Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-20 -mt-20 relative z-10">
        <div className="flex gap-10 items-start">
          {/* Table of Contents — desktop sidebar */}
          <TableOfContents sections={sections} />

          {/* Main article */}
          <div className="flex-1 min-w-0 max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="glass rounded-2xl p-8 md:p-12"
            >
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="glass text-[11px] font-semibold px-3 py-1 rounded-full text-cyber-blue">
                  {post.category}
                </span>
                <span className="text-gray-500 text-sm">{post.date}</span>
                {post.readingTime && (
                  <>
                    <span className="text-gray-500 text-sm">|</span>
                    <span className="text-gray-400 text-sm">
                      {post.readingTime}
                    </span>
                  </>
                )}
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">
                {post.title}
              </h1>

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {post.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/blog?tags=${tag}`}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium bg-tech-green/10 text-tech-green hover:bg-tech-green/20 transition-colors"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              )}

              {/* Share buttons — top */}
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/10">
                <span className="text-gray-500 text-sm font-medium">
                  Share this article
                </span>
                <ShareButtons title={post.title} />
              </div>

              {/* Content */}
              <div
                className="prose prose-invert max-w-none [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:text-lg [&_h3]:md:text-xl [&_h3]:font-semibold [&_h3]:text-white [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:text-gray-300 [&_p]:text-base [&_p]:leading-relaxed [&_p]:mb-4 [&_ul]:text-gray-300 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:text-gray-300 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1 [&_blockquote]:border-l-3 [&_blockquote]:border-cyber-blue [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-400 [&_blockquote]:my-6 [&_pre]:bg-black/40 [&_pre]:border [&_pre]:border-white/10 [&_pre]:rounded-xl [&_pre]:p-4 [&_pre]:overflow-x-auto [&_pre]:my-6 [&_code]:bg-cyber-blue/10 [&_code]:text-cyber-blue [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_img]:rounded-xl [&_img]:max-w-full [&_img]:my-6 [&_a]:text-cyber-blue [&_a]:underline [&_a]:hover:no-underline [&_hr]:border-white/10 [&_hr]:my-8"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Share buttons — bottom */}
              <div className="mt-12 pt-8 border-t border-white/10">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <Link
                    to="/blog"
                    className="inline-flex items-center gap-2 text-tech-green hover:text-cyber-blue transition-colors font-semibold"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="w-5 h-5 fill-current"
                    >
                      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                    </svg>
                    Back to Blog
                  </Link>
                  <ShareButtons title={post.title} />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Related Posts */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-20 mt-20">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl md:text-3xl font-bold mb-8">
              Related{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue to-tech-green">
                Articles
              </span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <BlogCard key={p.slug || p.id} post={p} />
              ))}
            </div>
          </motion.div>
        </section>
      )}
    </article>
  );
}
